import { format, isToday } from "date-fns";
import ElementWrapper from "features/VehicleSidebar/VehicleList/common/ElementWrapper";
import { trimStationName } from "features/VehicleSidebar/VehicleList/utils/stopNameUtils";
import { FC } from "react";
import { To } from "react-router-dom";
import { DropsTooltip, HStack, Stack, VStack } from "shared/components/Spor";
import { Text } from "shared/components/typography/TitleStyles";
import { formatDateString } from "shared/utils/dateTimeUtils";
import styled, { useTheme } from "styled-components";

const SMALL_BREAKPOINT = 480;
const SMALLER_BREAKPOINT = 379;

const ResponsiveContainer = styled.div`
  container-type: inline-size;
  width: 100%;
`;

const ShowOnLarge = styled.div`
  display: block;
  @container (max-width: ${SMALL_BREAKPOINT}px) {
    display: none;
  }
`;

const ShowOnSmall = styled.div`
  display: none;
  @container (max-width: ${SMALL_BREAKPOINT}px) {
    display: block;
  }
  @container (max-width: ${SMALLER_BREAKPOINT}px) {
    display: none;
  }
`;

const ShowOnSmaller = styled.div`
  display: none;
  @container (max-width: ${SMALLER_BREAKPOINT}px) {
    display: block;
  }
`;

export const DayPlanListBus = styled.div`
  gap: 6px;
  min-width: 100%;
  flex: 1;
`;

const DateSpan = styled.span`
  margin-left: 9px;
  color: ${({ theme }) => theme.colorTextSecondary};
  text-transform: capitalize;
`;

const Circle = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colorSuccess};
  border: 5px solid ${({ theme }) => theme.colorSuccessOutline};
  display: flex;
  align-items: center;
  justify-content: center;
`;

type DayPlanListProps = {
  title: string;
  subTitle: string | null;
  fromLocation: string | null;
  toLocation: string | null;
  startTime: Date | string | null;
  endTime: Date | string | null;
  isSelected: boolean;
  isBus?: boolean;
  to?: To;
};

export const DayPlanListItemBus: FC<DayPlanListProps> = ({
  title,
  subTitle,
  fromLocation,
  toLocation,
  startTime,
  endTime,
  isSelected = false,
  isBus,
  to,
}) => {
  const theme = useTheme();
  const isNotToday = startTime && !isToday(new Date(startTime));
  const NO_TIME = "--:--";

  const formatTime = (time: Date | string) =>
    time instanceof Date
      ? format(time, "HH:mm")
      : formatDateString(time, "HH:mm");

  const formattedStartTime = startTime ? formatTime(startTime) : NO_TIME;
  const formattedEndTime = endTime ? formatTime(endTime) : NO_TIME;

  const now = new Date();
  const start = startTime ? new Date(startTime) : null;
  const end = endTime ? new Date(endTime) : null;
  const isRunningNow = start && end && now >= start && now <= end;

  const dayPlanListItemContent = (
    <VStack alignItems="start" w="100%">
      <HStack justifyContent="space-between" alignItems="flex-start" w="100%">
        <HStack style={{ minWidth: "120px" }}>
          <Text bold>{title}</Text>
          {subTitle && <Text secondary>{subTitle}</Text>}
        </HStack>
        <HStack style={{ minWidth: "200px" }}>
          <Text truncated style={{ maxWidth: "100%" }}>
            {trimStationName(fromLocation) || "ukjent"}–
            {trimStationName(toLocation) || "ukjent"}
          </Text>
        </HStack>
        <HStack style={{ minWidth: "120px" }}>
          <Text style={{ whiteSpace: "nowrap" }}>
            {formattedStartTime}–{formattedEndTime}
            {isNotToday && (
              <DateSpan>({format(new Date(startTime), "dd.MM.yy")})</DateSpan>
            )}
          </Text>
        </HStack>
        <Stack style={{ minWidth: "20px" }}>
          {isBus && isRunningNow && (
            <DropsTooltip label="Kjøres nå" placement="top" hasArrow>
              <Circle />
            </DropsTooltip>
          )}
        </Stack>
      </HStack>
    </VStack>
  );

  const dayPlanListItemContentTwoColumn = (
    <VStack alignItems="start" w="100%">
      <HStack
        justifyContent="space-between"
        alignItems="flex-start"
        w="100%"
        style={{ minWidth: "120px" }}
      >
        <VStack w={12} alignItems="start">
          <HStack style={{ minWidth: "120px" }}>
            <Text bold>{title}</Text>
            {subTitle && <Text secondary>{subTitle}</Text>}
          </HStack>
          <Text>
            {formattedStartTime}–{formattedEndTime}
            {isNotToday && (
              <DateSpan>({format(new Date(startTime), "dd.MM.yy")})</DateSpan>
            )}
          </Text>
        </VStack>
        <VStack flex="1" alignItems="center" style={{ minWidth: "200px" }}>
          <Text truncated style={{ maxWidth: "100%" }}>
            {trimStationName(fromLocation) || "ukjent"}–
            {trimStationName(toLocation) || "ukjent"}
          </Text>
        </VStack>
        <Stack style={{ minWidth: "20px" }}>
          {isBus && isRunningNow && (
            <DropsTooltip label="Kjøres nå" placement="top" hasArrow>
              <Circle />
            </DropsTooltip>
          )}
        </Stack>
      </HStack>
    </VStack>
  );

  const dayPlanListItemContentOneColumn = (
    <VStack alignItems="start" w="100%">
      <HStack justifyContent="space-between" alignItems="flex-start" w="100%">
        <VStack w={12} alignItems="start">
          <HStack maxWidth="md">
            <Text bold>{title}</Text>
            {subTitle && <Text secondary>{subTitle}</Text>}
          </HStack>
          <Text>
            {trimStationName(fromLocation) || "ukjent"}–
            {trimStationName(toLocation) || "ukjent"}
          </Text>
          <Text>
            {formattedStartTime}–{formattedEndTime}
            {isNotToday && (
              <DateSpan>({format(new Date(startTime), "dd.MM.yy")})</DateSpan>
            )}
          </Text>
        </VStack>
        <Stack style={{ minWidth: "20px" }}>
          {isBus && isRunningNow && (
            <DropsTooltip label="Kjøres nå" placement="top" hasArrow>
              <Circle />
            </DropsTooltip>
          )}
        </Stack>
      </HStack>
    </VStack>
  );

  return (
    <ElementWrapper
      selected={isSelected}
      backgroundColor={theme.colorBackgroundTertiary}
      dashedColor="tertiary"
      isHover
      to={to}
    >
      <ResponsiveContainer>
        <ShowOnLarge>{dayPlanListItemContent}</ShowOnLarge>
        <ShowOnSmall>{dayPlanListItemContentTwoColumn}</ShowOnSmall>
        <ShowOnSmaller>{dayPlanListItemContentOneColumn}</ShowOnSmaller>
      </ResponsiveContainer>
    </ElementWrapper>
  );
};
